import { FC, useState, useEffect } from 'react';
import {
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  Box,
  Typography,
  InputAdornment,
} from '@mui/material';
import { countries, getEmojiFlag, TCountryCode } from 'countries-list';
import { CountryPickerProps } from 'src/models/FieldProps.interface';
import { FieldType } from 'src/models/enums/FieldType.enum';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';

const CountryPicker: FC<CountryPickerProps> = ({
  label,
  value,
  onChange,
  error,
  touched,
  height = '40px',
  width = '450px',
  sx,
  disabled,
  type = FieldType.SELECT,
  isEditable: initialIsEditable = false,
}) => {
  const [isEditable, setIsEditable] = useState(initialIsEditable);

  useEffect(() => {
    setIsEditable(initialIsEditable);
  }, [initialIsEditable]);

  const handleEditClick = () => {
    setIsEditable(!isEditable);
  };

  const countryList = Object.keys(countries).map((code) => ({
    name: countries[code as TCountryCode].name,
    code,
  }));

  const sortedCountryList = countryList.sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <>
      <Typography
        fontFamily="Outfit"
        fontWeight="400"
        fontSize="16px"
        color="var(--primary-navy)"
        mb={2}
      >
        {label}
      </Typography>
      <FormControl fullWidth error={touched && Boolean(error)} sx={{ m: 0 }}>
        <Select
          labelId="country-select-label"
          id="country-select"
          value={value}
          onChange={onChange}
          disabled={disabled}
          readOnly={type === FieldType.EDIT && !isEditable}
          startAdornment={
            type === FieldType.EDIT && (
              <InputAdornment position="start">
                <Box
                  component="i"
                  className="bx bx-edit-alt"
                  sx={{
                    fontSize: '16px',
                    color: 'var(--instructions)',
                    cursor: 'pointer',
                    marginLeft: '8px',
                  }}
                  onClick={handleEditClick}
                />
              </InputAdornment>
            )
          }
          IconComponent={ExpandMoreOutlined}
          sx={{
            height: height,
            width: width,
            bgcolor: 'var(--text-bg)',
            border: 'none',
            borderRadius: 1,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--border)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-blue)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-blue)',
            },
            '& .MuiSelect-select': {
              fontFamily: 'Outfit',
              fontSize: '16px',
            },
            overflow: 'hidden',
            '& input:-webkit-autofill': {
              bgcolor: 'var(--bg-color)',
              WebkitBoxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            '& input:-moz-autofill': {
              bgcolor: 'var(--bg-color)',
              boxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            '& input:-ms-autofill': {
              bgcolor: 'var(--bg-color)',
              boxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            '&.Mui-disabled': {
              color: 'var(--instructions)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--border)',
              },
              '& .MuiSelect-select': {
                color: 'var(--primary-blue)',
              },
              '& .MuiSelect-icon': {
                display: 'none',
              },
            },
            ...sx,
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: '300px',
                backgroundColor: 'var(--bg-color)',
                overflowY: 'auto',
              },
              sx: {
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'var(--bg-color)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'var(--loader-color)',
                  borderRadius: '4px',
                  height: '70px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: 'var(--primary-blue)',
                  cursor: 'pointer',
                },
              },
            },
          }}
        >
          {sortedCountryList.map((country) => (
            <MenuItem
              key={country.name}
              value={country.name}
              sx={{
                fontFamily: 'Outfit',
                fontSize: '16px',
                '&.Mui-selected': {
                  backgroundColor: 'var(--inactive-btn)',
                  color: 'var(--primary-blue)',
                },
              }}
            >
              <span style={{ position: 'absolute', left: '-9999px' }}>
                {country.name}
              </span>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ fontSize: '20px', marginRight: 1 }}>
                  {getEmojiFlag(country.code as TCountryCode)}
                </Box>
                {country.name}
              </Box>
            </MenuItem>
          ))}
        </Select>
        {touched && error ? (
          <FormHelperText
            sx={{
              fontFamily: 'Outfit',
              fontWeight: '400',
              fontSize: '14px',
              color: 'var(--error)',
              display: 'flex',
              alignItems: 'center',
              ml: 0,
              mt: 1,
            }}
          >
            <Box
              component="i"
              className="bx bxs-error-circle"
              sx={{
                fontSize: '16px',
                color: 'var(--error)',
                mr: 0.8,
              }}
            />
            {error}
          </FormHelperText>
        ) : null}
      </FormControl>
    </>
  );
};

export default CountryPicker;
